/**
 * Notification preferences — shape, defaults, and friendly labels.
 *
 * Source of truth for both the Account settings UI and the email
 * Cloud Functions (Functions/email.js mirrors the same NOTIFICATION_TYPES list).
 *
 * Storage: `users/{uid}.notificationPreferences` — see docs/email-notifications-plan.md.
 */

import type { NotificationType } from './notification.service';

export const NOTIFICATION_TYPES_ORDER: NotificationType[] = [
  'deliveryNotifications',
  'deliveryConfirmations',
  'deliveryRejections',
  'reviewNotifications',
  'accessRequests',
  'accessRequestResults',
  'ftpAccessRequests',
  'ftpAccessResults',
];

/** Friendly label shown in the settings table for each type. */
export const NOTIFICATION_LABELS: Record<NotificationType, string> = {
  deliveryNotifications:  'Tracks delivered to my organisation',
  deliveryConfirmations:  'My campaign was delivered (with report PDF)',
  deliveryRejections:     'My delivery was rejected',
  reviewNotifications:    'My track review was answered',
  accessRequests:         'Someone requested access to my track',
  accessRequestResults:   'My access request was answered',
  ftpAccessRequests:      'Studio requested FTP access',
  ftpAccessResults:       'My FTP access request was answered',
};

export type ChannelPrefs = Record<NotificationType, boolean>;
/** Per-user, per-type "enabled since" timestamps. A notification is shown
 *  ONLY when the user's inApp pref is true AND its createdAt >= this
 *  timestamp. Set to `Date.now()` on the transition from OFF→ON so that
 *  turning a notification type back on after a period of quiet doesn't
 *  flood the user with the accumulated backlog. `0` means "show
 *  everything" (default state — no filter). Because prefs are stored
 *  per-user on the member doc, one user's timestamp never affects
 *  another member of the same org. */
export type EnabledSince = Record<NotificationType, number>;

export interface NotificationPreferences {
  inApp: ChannelPrefs;
  email: ChannelPrefs;
  inAppEnabledSince: EnabledSince;
}

/** Default prefs applied when a user has none stored. In-app on, email off. */
export function defaultPreferences(): NotificationPreferences {
  return {
    inApp: {
      deliveryNotifications:  true,
      deliveryConfirmations:  true,
      deliveryRejections:     true,
      reviewNotifications:    true,
      accessRequests:         true,
      accessRequestResults:   true,
      ftpAccessRequests:      true,
      ftpAccessResults:       true,
    },
    email: {
      deliveryNotifications:  false,
      deliveryConfirmations:  false,
      deliveryRejections:     false,
      reviewNotifications:    false,
      accessRequests:         false,
      accessRequestResults:   false,
      ftpAccessRequests:      false,
      ftpAccessResults:       false,
    },
    inAppEnabledSince: {
      deliveryNotifications:  0,
      deliveryConfirmations:  0,
      deliveryRejections:     0,
      reviewNotifications:    0,
      accessRequests:         0,
      accessRequestResults:   0,
      ftpAccessRequests:      0,
      ftpAccessResults:       0,
    },
  };
}

/**
 * Merge stored prefs with defaults — defensive against partial / missing
 * preference docs. Always returns a fully-populated NotificationPreferences.
 */
export function mergeWithDefaults(stored: any): NotificationPreferences {
  const def = defaultPreferences();
  if (!stored || typeof stored !== 'object') return def;
  const merged: NotificationPreferences = { ...def };
  for (const channel of ['inApp', 'email'] as const) {
    const channelStored = (stored[channel] && typeof stored[channel] === 'object') ? stored[channel] : {};
    merged[channel] = { ...def[channel] };
    for (const type of NOTIFICATION_TYPES_ORDER) {
      if (typeof channelStored[type] === 'boolean') {
        merged[channel][type] = channelStored[type];
      }
    }
  }
  // enabled-since timestamps — legacy docs have none, default to 0 (no filter).
  const sinceStored = (stored.inAppEnabledSince && typeof stored.inAppEnabledSince === 'object')
    ? stored.inAppEnabledSince : {};
  merged.inAppEnabledSince = { ...def.inAppEnabledSince };
  for (const type of NOTIFICATION_TYPES_ORDER) {
    if (typeof sinceStored[type] === 'number' && Number.isFinite(sinceStored[type])) {
      merged.inAppEnabledSince[type] = sinceStored[type];
    }
  }
  return merged;
}
